import { z } from 'zod';

import { trackedAccountRowSchema } from '../accounts/api/schemas';

export const levelOutSchema = z.object({
  id: z.uuid(),
  position: z.number().int(),
  name: z.string(),
  teams: z.number().int(),
});
export type LevelOut = z.infer<typeof levelOutSchema>;

export const teamOutSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  level_id: z.uuid(),
  level_position: z.number().int(),
  level_name: z.string(),
  parent_team_id: z.uuid().nullable(),
  lead_name: z.string().nullable(),
  lead_account_id: z.uuid().nullable(),
  direct_accounts: z.number().int(),
  subtree_accounts: z.number().int(),
});
export type TeamOut = z.infer<typeof teamOutSchema>;

/**
 * `teamOutSchema` plus the five figures the structure read carries per team (owner ruling,
 * `owner-decisions-align-per-team-figures-have-no-producer`): the lead's resolved identity and
 * four roll-ups over the team's own subtree. `reach` has no producer anywhere in the platform yet
 * and is always `null` -- never rendered as `0`.
 */
export const teamFiguresOutSchema = teamOutSchema.extend({
  lead_identity: z.string().nullable(),
  members: z.number().int(),
  reach: z.number().int().nullable(),
  posts: z.number().int(),
  sentiment_delta: z.number().int(),
});
export type TeamFiguresOut = z.infer<typeof teamFiguresOutSchema>;

export const structureOutSchema = z.object({
  levels: z.array(levelOutSchema),
  teams: z.array(teamFiguresOutSchema),
  unassigned_accounts: z.number().int(),
});
export type StructureOut = z.infer<typeof structureOutSchema>;

export const levelInSchema = z.object({
  name: z.string(),
});
export type LevelIn = z.infer<typeof levelInSchema>;

export const teamInSchema = z.object({
  level_id: z.uuid(),
  name: z.string(),
  parent_team_id: z.uuid().nullable().optional(),
  lead_name: z.string().nullable().optional(),
  lead_account_id: z.uuid().nullable().optional(),
});
export type TeamIn = z.infer<typeof teamInSchema>;

export const teamAssignmentInSchema = z.object({
  team_id: z.uuid().nullable(),
});
export type TeamAssignmentIn = z.infer<typeof teamAssignmentInSchema>;

export const teamUpdateInSchema = z.object({
  name: z.string().optional(),
  parent_team_id: z.uuid().nullable().optional(),
  lead_name: z.string().nullable().optional(),
  lead_account_id: z.uuid().nullable().optional(),
});
export type TeamUpdateIn = z.infer<typeof teamUpdateInSchema>;

export const levelRemovalOutSchema = z.object({
  teams_removed: z.number().int(),
  accounts_moved: z.number().int(),
  accounts_unassigned: z.number().int(),
});
export type LevelRemovalOut = z.infer<typeof levelRemovalOutSchema>;

/** `GET .../tracked-accounts` as `TeamAccounts` reads it: the same row `accounts/api/schemas.ts`
 *  owns, plus `team_id` (`align-team-editor-api-account-team`) so a search result can tell
 *  whether a candidate is already in another team, and the direct-accounts list can filter to
 *  this one. */
export const teamTrackedAccountRowSchema = trackedAccountRowSchema.extend({
  team_id: z.uuid().nullable(),
});
export type TeamTrackedAccountRow = z.infer<typeof teamTrackedAccountRowSchema>;

export const teamTrackedAccountListSchema = z.object({
  items: z.array(teamTrackedAccountRowSchema),
});

/** `PUT /campaigns/{id}/levels`'s whole-structure write: an ordered entry per kept level
 *  (`id` set, may carry a new `name`) or appended one (`id` null), in the order they save
 *  in -- a missing entry removes that level. `?preview=true` on the same route answers with
 *  `levelRemovalOutSchema` and applies nothing. */
export const levelBatchEntryInSchema = z.object({
  id: z.uuid().nullable(),
  name: z.string(),
});
export type LevelBatchEntryIn = z.infer<typeof levelBatchEntryInSchema>;

export const levelBatchInSchema = z.object({
  levels: z.array(levelBatchEntryInSchema),
});
export type LevelBatchIn = z.infer<typeof levelBatchInSchema>;

export {
  levelOutSchema as LevelOutSchema,
  teamOutSchema as TeamOutSchema,
  teamFiguresOutSchema as TeamFiguresOutSchema,
  structureOutSchema as StructureOutSchema,
  levelInSchema as LevelInSchema,
  teamInSchema as TeamInSchema,
  teamAssignmentInSchema as TeamAssignmentInSchema,
  levelRemovalOutSchema as LevelRemovalOutSchema,
};
